import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import RaportDetailsTemp from '@/routes/ReportDetails/raport-details-temp';
import { PinInput } from '@/components/PinInput';
import type { PinInputSubmitPayload } from '@/components/PinInput/PinInput.types';
import { reportService } from '@/api/services/report.service';
import { apiService } from '@/api/services/api.service';
import type { ApiRequestError } from '@/api/types/api.types';
import { Layout } from '@/components/Layout/Layout';
import { Footer } from '@/components/Footer';
import {
  getBrandFavicon,
  getBrandLogo,
  resolveAccessLinkContact,
  resolveAccessLinkLogoSrc,
  type AccessLinkBrandState,
} from '@/helpers/brandLogo';
import { useFavicon } from '@/hooks/useFavicon';

const LINK_ID_STORAGE_KEY = 'reportAccessLinkId';
const REPORT_ID_STORAGE_KEY = 'reportAccessReportId';

const messageForError = (err: ApiRequestError): string => {
  switch (err.error) {
    case 'INCORRECT_CREDENTIALS':
      return 'Nieprawidłowy email lub PIN.';
    case 'ENTITY_NOT_FOUND':
      return 'Link nie istnieje lub jest nieaktywny.';
    case 'VALIDATION_ERROR':
    case 'BAD_REQUEST':
      return err.message || 'Sprawdź poprawność wprowadzonych danych.';
    default:
      if (err.status === 410 || err.status === 500) {
        return 'Link wygasł — poproś o nowy.';
      }
      return err.message || 'Wystąpił błąd podczas weryfikacji.';
  }
};

export const ReportAccess = () => {
  const { linkId } = useParams<{ linkId: string }>();

  // Gdy istnieje aktywna sesja dla tego linku, trzymamy tu id raportu i
  // renderujemy raport pod tym samym adresem `/report-access/:linkId`
  // (logowanie i raport pod jednym adresem). [869dz4tyc]
  const [reportId, setReportId] = useState<string | null>(() => {
    const savedLinkId = sessionStorage.getItem(LINK_ID_STORAGE_KEY);
    const savedReportId = sessionStorage.getItem(REPORT_ID_STORAGE_KEY);
    return apiService.getAuthToken() && savedLinkId === linkId && savedReportId
      ? savedReportId
      : null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendInfo, setResendInfo] = useState<string | null>(null);
  const [brandState, setBrandState] = useState<AccessLinkBrandState>({
    status: 'loading',
  });
  const fallbackFavicon = getBrandFavicon();
  const brandFavicon = getBrandFavicon(
    brandState.status === 'resolved' ? brandState.brandType : undefined,
  );

  // Po udanym PIN-ie osadzony RaportDetailsTemp przejmuje faviconę z danych
  // raportu; ekran dostępu odzyskuje swoją markę, gdy raport się odmontuje.
  useFavicon(reportId ? fallbackFavicon : brandFavicon, fallbackFavicon);

  // Branding nagłówka: pobieramy markę linku po samym `linkId` (bez email/PIN),
  // żeby pokazać właściwe logo zanim klient się zaloguje. Do czasu pobrania
  // nagłówek nie pokazuje logo; przy błędzie wraca do domyślnego logo MC.
  useEffect(() => {
    if (!linkId) return;
    let cancelled = false;
    setBrandState({ status: 'loading' });
    reportService
      .getAccessLinkBrand(linkId)
      .then((response) => {
        if (cancelled) return;
        setBrandState({
          status: 'resolved',
          brandType: response.data?.brandType,
          contactEmail: response.data?.contactEmail,
          contactPhone: response.data?.contactPhone,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setBrandState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [linkId]);

  const headerLogoSrc = resolveAccessLinkLogoSrc(brandState);
  // Stopka jest zawsze widoczna: logo i kontakt domyślnie MC, podmieniane na
  // dane brandu dopiero po rozwiązaniu marki (bez migania pustymi wartościami).
  const footerLogoSrc = getBrandLogo(
    brandState.status === 'resolved' ? brandState.brandType : undefined,
  );
  const footerContact = resolveAccessLinkContact(brandState);

  useEffect(() => {
    if (!linkId) return;
    const savedLinkId = sessionStorage.getItem(LINK_ID_STORAGE_KEY);
    const savedReportId = sessionStorage.getItem(REPORT_ID_STORAGE_KEY);

    if (savedLinkId && savedLinkId !== linkId) {
      // Nowy link dla innego raportu — wyczyść poprzednią sesję,
      // żeby user nie zobaczył starego raportu po (nie)udanej weryfikacji.
      apiService.setAuthToken(null);
      sessionStorage.removeItem(LINK_ID_STORAGE_KEY);
      sessionStorage.removeItem(REPORT_ID_STORAGE_KEY);
      setReportId(null);
      return;
    }

    if (
      apiService.getAuthToken() &&
      savedLinkId === linkId &&
      savedReportId
    ) {
      // Aktywna sesja — pokaż raport w miejscu, bez zmiany adresu.
      setReportId(savedReportId);
    }
  }, [linkId]);

  // Wygaśnięcie/utrata tokenu (np. 401 podczas pobierania raportu) — wróć do
  // ekranu logowania pod tym samym adresem linku dostępowego.
  useEffect(() => apiService.onAuthExpired(() => setReportId(null)), []);

  const handleSubmit = useCallback(
    async ({ email, pin }: PinInputSubmitPayload) => {
      if (!linkId) return;
      setLoading(true);
      setError(null);
      setResendInfo(null);
      try {
        const response = await reportService.verifyAccessLink(
          linkId,
          email,
          pin,
        );
        const resolvedReportId = response.data?.report?.id;
        if (!resolvedReportId) {
          setError('Nie udało się odczytać identyfikatora raportu.');
          return;
        }
        sessionStorage.setItem(LINK_ID_STORAGE_KEY, linkId);
        sessionStorage.setItem(REPORT_ID_STORAGE_KEY, resolvedReportId);
        // Zostajemy pod adresem linku dostępowego i renderujemy raport w
        // miejscu — udostępniany URL to zawsze link logowania. [869dz4tyc]
        setReportId(resolvedReportId);
      } catch (err) {
        setError(messageForError(err as ApiRequestError));
      } finally {
        setLoading(false);
      }
    },
    [linkId],
  );

  const handleResend = useCallback(
    async (email: string) => {
      if (!linkId) return;
      setResendLoading(true);
      setError(null);
      setResendInfo(null);
      try {
        await reportService.resendPin(linkId, email);
        setResendInfo(
          'Nowy PIN został wysłany na podany adres email (jeśli pasuje do linku). Stary PIN przestał działać.',
        );
      } catch (err) {
        setError(messageForError(err as ApiRequestError));
      } finally {
        setResendLoading(false);
      }
    },
    [linkId],
  );

  if (!linkId) {
    return (
      <Layout title="Dostęp do raportu">
        <div className="flex justify-center items-center min-h-[60vh] px-4">
          <div className="max-w-md text-center">
            <h1 className="mb-3 text-2xl font-semibold text-text-primary">
              Otwórz link z wiadomości email
            </h1>
            <p className="text-gray-600">
              Aby uzyskać dostęp do raportu, skorzystaj z linku przesłanego na
              Twój adres email.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  // Po zalogowaniu raport jest renderowany pod tym samym adresem
  // `/report-access/:linkId` — bez przekierowania na `/raporty/:id`. [869dz4tyc]
  if (reportId) {
    return <RaportDetailsTemp reportId={reportId} />;
  }

  return (
    <Layout
      title=""
      headerLogoSrc={headerLogoSrc}
      footerContent={<Footer logoSrc={footerLogoSrc} contact={footerContact} />}
    >
      <PinInput
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        onResendPin={handleResend}
        resendLoading={resendLoading}
        resendInfo={resendInfo}
      />
    </Layout>
  );
};

export default ReportAccess;
